/** Refined Navbar — utility bar + glass-pill nav + hover dropdowns + mobile drawer.
 *  Builds on Prompt 1 shell; adds: sub-menus, scroll-shadow, search dialog trigger.
 */
import { useEffect, useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { Menu, X, Search, ShoppingCart, Flame, Sparkles, BookOpen, Compass, Star, Infinity, ChevronDown, Bell, MessageCircle, HeartHandshake } from "lucide-react";
import { Brand } from "../ui/Brand";
import { Button } from "../ui/Button";
import { ThemeToggle } from "../ui/ThemeToggle";
import { CurrencySwitcher } from "../ui/CurrencySwitcher";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/Dialog";
import { SearchInput } from "../ui/SearchInput";
import { UserMenu } from "../../features/auth/UserMenu";
import { useAuth } from "../../features/auth/AuthContext";
import { useCart } from "../../features/cart/CartContext";
import { PRIMARY_NAV } from "./nav-config";
import { cn } from "../../lib/cn";
import { api } from "../../lib/api";
import { usePublicSettings, whatsappHref } from "../../lib/usePublicSettings";

function CartCountBadge() {
  const { count } = useCart();
  if (!count) return null;
  return <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-mauve text-white text-[10px] font-bold inline-flex items-center justify-center" data-testid="cart-count-badge">{count}</span>;
}

function SearchDialogBody({ onClose }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const nav = useNavigate();

  useEffect(() => {
    if (q.trim().length < 2) { setResults(null); return; }
    setLoading(true);
    const id = setTimeout(() => {
      api.get(`/search?q=${encodeURIComponent(q.trim())}&limit_per=4`)
        .then((r) => setResults(r.data))
        .catch(() => setResults({ results: {}, total: 0 }))
        .finally(() => setLoading(false));
    }, 250);
    return () => clearTimeout(id);
  }, [q]);

  const onPick = (url) => { onClose(); nav(url); };
  const groups = results ? Object.entries(results.results).filter(([, v]) => v.length > 0) : [];
  const kindLabel = { course: "Courses", sadhana: "Sadhana", product: "Products", research: "Research", book: "Library", service: "Services", page: "Pages" };

  return (
    <div data-testid="search-dialog">
      <SearchInput autoFocus placeholder="Search courses, sadhanas, products, research, books…"
        value={q} onChange={(e) => setQ(e.target.value)} testId="global-search-input" />
      <div className="mt-5 max-h-[60vh] overflow-y-auto" data-testid="search-results">
        {q.trim().length < 2 && (
          <p className="text-[12.5px] text-text-faint font-mono uppercase tracking-[0.16em]">Type at least 2 characters to search…</p>
        )}
        {q.trim().length >= 2 && loading && <p className="text-[13px] text-text-dim">Searching…</p>}
        {q.trim().length >= 2 && !loading && results?.total === 0 && (
          <p className="text-[13px] text-text-dim">No matches for <b className="text-text">"{q}"</b>.</p>
        )}
        {groups.map(([kind, items]) => (
          <div key={kind} className="mb-5" data-testid={`search-group-${kind}`}>
            <div className="text-[10.5px] font-mono uppercase tracking-[0.2em] text-text-faint mb-2">{kindLabel[kind] || kind}</div>
            <ul className="flex flex-col gap-1">
              {items.map((it) => (
                <li key={it.id}>
                  <button onClick={() => onPick(it.url)} data-testid={`search-result-${it.slug}`}
                    className="w-full text-left px-3 py-2.5 rounded-xl hover:bg-surface-2 border border-transparent hover:border-line-2 transition-colors">
                    <div className="text-[13.5px] font-semibold">{it.title}</div>
                    {it.summary && <div className="text-[12px] text-text-dim line-clamp-2 mt-0.5">{it.summary}</div>}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
      <p className="text-[12px] text-text-faint mt-4 font-mono uppercase tracking-[0.16em]">
        Tip — press <kbd className="px-1.5 py-0.5 rounded bg-surface-2 border border-line text-text">⌘K</kbd> anywhere
      </p>
    </div>
  );
}

function NotifBell() {
  const [unread, setUnread] = useState(0);
  useEffect(() => {
    let cancelled = false;
    const fetchCount = () => api.get("/notifications?unread=true&limit=1").then((r) => { if (!cancelled) setUnread(r.data.unread || 0); }).catch(() => {});
    fetchCount();
    const t = setInterval(fetchCount, 60000);
    return () => { cancelled = true; clearInterval(t); };
  }, []);
  return (
    <Link
      to="/dashboard/notifications"
      aria-label="Notifications"
      data-testid="nav-notif-link"
      className="hidden sm:inline-flex items-center justify-center relative w-10 h-10 rounded-full border border-line-2 bg-surface text-text hover:border-mauve hover:bg-surface-2 transition-colors"
    >
      <Bell size={18} />
      {unread > 0 && <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-mauve text-white text-[10px] font-bold inline-flex items-center justify-center" data-testid="notif-count-badge">{unread > 9 ? "9+" : unread}</span>}
    </Link>
  );
}

const ICONS = {
  lotus: BookOpen,
  flame: Flame,
  compass: Compass,
  star: Star,
  sparkle: Sparkles,
  infinity: Infinity,
  whatsapp: MessageCircle,
  donate: HeartHandshake,
};

// About menu gains WhatsApp / Donate only when configured in Admin → Settings → about_links.
function useNavItems() {
  const settings = usePublicSettings();
  const links = settings?.about_links;
  const wa = whatsappHref(links);
  const extra = [
    wa && { label: "WhatsApp", href: wa, desc: "Chat with our team", icon: "whatsapp" },
    links?.donate_url && { label: "Donate", href: links.donate_url, desc: "Support the mission", icon: "donate" },
  ].filter(Boolean);
  return PRIMARY_NAV.map((it) => (it.label === "About" ? { ...it, children: [...it.children, ...extra] } : it));
}

function ChildLink({ child, className, children, ...rest }) {
  if (child.href) return <a href={child.href} target="_blank" rel="noopener noreferrer" className={className} {...rest}>{children}</a>;
  return <Link to={child.to} className={className} {...rest}>{children}</Link>;
}

const childTestId = (label) => label.toLowerCase().replace(/\s+/g, "-");

function NavLinkItem({ item, location }) {
  const active = location.pathname === item.to || (item.to !== "/" && location.pathname.startsWith(item.to));
  if (!item.children) {
    return (
      <NavLink
        to={item.to}
        end={item.to === "/"}
        data-testid={`nav-link-${item.label.toLowerCase()}`}
        className={({ isActive }) =>
          cn(
            "px-3 py-2 text-[14px] font-medium rounded-full transition-colors",
            isActive ? "text-text bg-surface-2" : "text-text-dim hover:text-text"
          )
        }
      >
        {item.label}
      </NavLink>
    );
  }
  return (
    <div className="relative group" data-testid={`nav-group-${item.label.toLowerCase()}`}>
      <NavLink
        to={item.to}
        className={cn(
          "px-3 py-2 text-[14px] font-medium rounded-full transition-colors inline-flex items-center gap-1",
          active ? "text-text bg-surface-2" : "text-text-dim hover:text-text"
        )}
      >
        {item.label}
        <ChevronDown size={12} className="transition-transform duration-300 group-hover:rotate-180" />
      </NavLink>
      <div
        className="absolute left-0 top-full pt-3 hidden group-hover:block group-focus-within:block z-50"
        data-testid={`nav-dropdown-${item.label.toLowerCase()}`}
      >
        <div className="min-w-[320px] p-2 rounded-2xl border border-line-2 bg-glass-2 backdrop-blur-xl shadow-float">
          {item.children.map((child) => {
            const Icon = ICONS[child.icon] || Compass;
            return (
              <ChildLink
                key={child.label}
                child={child}
                data-testid={`nav-child-${childTestId(child.label)}`}
                className="flex items-start gap-3 px-3 py-2.5 rounded-xl hover:bg-surface-2 transition-colors"
              >
                <span className="w-8 h-8 rounded-full border border-line-2 bg-surface grid place-items-center text-mauve flex-shrink-0">
                  <Icon size={14} />
                </span>
                <span className="flex flex-col leading-tight">
                  <b className="text-[13.5px] font-semibold text-text">{child.label}</b>
                  <span className="text-[12px] text-text-faint">{child.desc}</span>
                </span>
              </ChildLink>
            );
          })}
        </div>
      </div>
    </div>
  );
}

export function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const location = useLocation();
  const { user } = useAuth();
  const isAuthed = !!user && user !== false && user !== null;
  const navItems = useNavItems();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Cmd/Ctrl+K opens search
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setSearchOpen(true);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    document.body.style.overflow = mobileOpen ? "hidden" : "";
  }, [mobileOpen]);

  return (
    <>
      <div
        className={cn(
          "fixed top-0 left-0 right-0 z-[100] transition-[padding] duration-300 ease-nvl",
          scrolled ? "pt-[10px]" : "pt-4"
        )}
        style={{ paddingLeft: "var(--gut)", paddingRight: "var(--gut)" }}
        data-testid="navbar-wrap"
      >
        {/* Gayatri Mantra ticker — scrolling marquee */}
        <div className="max-w-[1280px] mx-auto px-4 hidden md:block overflow-hidden mb-2" data-testid="gayatri-mantra-strip">
          <div className="nvl-mantra-track text-[11.5px] text-text-faint font-mono tracking-[0.32em] uppercase">
            <span className="nvl-mantra-row">
              <span className="mx-8">Om Bhur Bhuvah Swaha</span>
              <span className="mx-8 text-mauve/70">·</span>
              <span className="mx-8">Tat Savitur Varenyam</span>
              <span className="mx-8 text-mauve/70">·</span>
              <span className="mx-8">Bhargo Devasya Dheemahi</span>
              <span className="mx-8 text-mauve/70">·</span>
              <span className="mx-8">Dhiyo Yo Nah Prachodayat</span>
              <span className="mx-8 text-mauve/70">·</span>
            </span>
            <span className="nvl-mantra-row" aria-hidden="true">
              <span className="mx-8">Om Bhur Bhuvah Swaha</span>
              <span className="mx-8 text-mauve/70">·</span>
              <span className="mx-8">Tat Savitur Varenyam</span>
              <span className="mx-8 text-mauve/70">·</span>
              <span className="mx-8">Bhargo Devasya Dheemahi</span>
              <span className="mx-8 text-mauve/70">·</span>
              <span className="mx-8">Dhiyo Yo Nah Prachodayat</span>
              <span className="mx-8 text-mauve/70">·</span>
            </span>
          </div>
        </div>

        {/* Glass pill nav */}
        <nav
          aria-label="Primary"
          className={cn(
            "max-w-[1280px] mx-auto flex items-center gap-3 pl-6 pr-3 py-[11px] rounded-pill backdrop-blur-xl border border-line-2 shadow-float transition-[background] duration-300 ease-nvl",
            scrolled ? "bg-glass-2" : "bg-glass"
          )}
          data-testid="primary-nav"
        >
          <Link to="/" aria-label="Nikhil Vatika Lab home" className="mr-2">
            <Brand />
          </Link>

          <div className="hidden xl:flex items-center gap-0.5 ml-auto mr-2">
            {navItems.map((item) => (
              <NavLinkItem key={item.to} item={item} location={location} />
            ))}
          </div>

          <div className="flex items-center gap-2 ml-auto xl:ml-0">
            <button
              aria-label="Search (⌘K)"
              data-testid="nav-search-btn"
              onClick={() => setSearchOpen(true)}
              className="hidden sm:inline-flex items-center justify-center w-10 h-10 rounded-full border border-line-2 bg-surface text-text hover:border-mauve hover:bg-surface-2 transition-colors"
            >
              <Search size={18} />
            </button>
            <ThemeToggle className="hidden sm:inline-flex" />
            <CurrencySwitcher className="hidden md:block" />
            <Link
              to="/cart"
              aria-label="Cart"
              data-testid="nav-cart-link"
              className="hidden sm:inline-flex items-center justify-center relative w-10 h-10 rounded-full border border-line-2 bg-surface text-text hover:border-mauve hover:bg-surface-2 transition-colors"
            >
              <ShoppingCart size={18} />
              <CartCountBadge />
            </Link>
            {isAuthed ? (
              <>
                <NotifBell />
                <UserMenu className="hidden md:block" />
              </>
            ) : (
              <>
                <Link
                  to="/login"
                  className="hidden md:inline-block text-[14px] font-medium text-text-dim hover:text-text px-3"
                  data-testid="nav-login-link"
                >
                  Login
                </Link>
                <Button asChild size="sm" className="hidden md:inline-flex" data-testid="nav-cta-btn">
                  <Link to="/register">Start Learning →</Link>
                </Button>
              </>
            )}
            <button
              aria-label="Open menu"
              data-testid="nav-burger-btn"
              onClick={() => setMobileOpen(true)}
              className="xl:hidden inline-flex items-center justify-center w-10 h-10 rounded-full border border-line-2 bg-surface text-text hover:border-mauve transition-colors"
            >
              <Menu size={20} />
            </button>
          </div>
        </nav>
      </div>

      {/* Search dialog */}
      <Dialog open={searchOpen} onOpenChange={setSearchOpen}>
        <DialogContent className="max-w-[680px] p-6">
          <DialogHeader>
            <DialogTitle>Search NVL</DialogTitle>
          </DialogHeader>
          <SearchDialogBody onClose={() => setSearchOpen(false)} />
        </DialogContent>
      </Dialog>

      {/* Mobile drawer */}
      <div
        className={cn(
          "fixed inset-0 z-[110] backdrop-blur-2xl flex-col items-stretch transition-opacity duration-300 ease-nvl",
          mobileOpen ? "flex opacity-100" : "hidden opacity-0"
        )}
        style={{ background: "var(--glass-2)" }}
        data-testid="mobile-menu"
      >
        <div className="flex items-center justify-between px-6 pt-6">
          <Brand />
          <button
            aria-label="Close menu"
            data-testid="mobile-menu-close"
            onClick={() => setMobileOpen(false)}
            className="inline-flex items-center justify-center w-10 h-10 rounded-full border border-line-2 bg-surface text-text"
          >
            <X size={20} />
          </button>
        </div>
        <div className="flex-1 flex flex-col gap-1 px-6 pt-10 overflow-y-auto">
          {navItems.map((item) => (
            <div key={item.to} className="border-b border-line">
              <NavLink
                to={item.to}
                end={item.to === "/"}
                data-testid={`mobile-nav-link-${item.label.toLowerCase()}`}
                className={({ isActive }) =>
                  cn("block py-4 text-[26px] font-display transition-colors", isActive ? "text-mauve" : "text-text hover:text-mauve")
                }
              >
                {item.label}
              </NavLink>
              {item.label === "About" && (
                <div className="flex flex-wrap gap-2 pb-4" data-testid="mobile-about-links">
                  {item.children.map((child) => (
                    <ChildLink key={child.label} child={child} onClick={() => setMobileOpen(false)}
                      data-testid={`mobile-nav-child-${childTestId(child.label)}`}
                      className="px-3.5 py-2 rounded-full border border-line-2 bg-surface text-[13px] text-text-dim hover:text-text hover:border-mauve transition-colors">
                      {child.label}
                    </ChildLink>
                  ))}
                </div>
              )}
            </div>
          ))}
          <div className="flex items-center gap-3 mt-8">
            <Button asChild variant="ghost" className="flex-1">
              <Link to="/login">Login</Link>
            </Button>
            <Button asChild className="flex-1">
              <Link to="/courses">Start Learning →</Link>
            </Button>
          </div>
          <div className="flex items-center gap-3 mt-6">
            <ThemeToggle />
            <CurrencySwitcher />
          </div>
        </div>
      </div>
    </>
  );
}
